import React, { useEffect, useMemo, useState } from 'react';
import { X, ChevronLeft, ChevronRight, Paperclip, AlertTriangle } from 'lucide-react';
import { expenseAPI } from '../../services/api';
import useDialog from '../../hooks/useDialog';
import TablePager, { usePagedRows } from '../TablePager';
import { onActivateKey, ROW_FOCUS_RING } from '../../utils/keyboard';
import EntryDetailModal from './EntryDetailModal';
import {
  formatCurrency, formatDate, formatSignedAmount, referenceOf, TypeBadge, PaymentBadge, UrduAmount,
  LoadingRow, errorText,
} from './shared';

// One month of the Cash Position: how it opened, what came in and went out, where the spending
// went, and every entry in date order with the cash in hand after it, so the day a balance went
// below zero can be found. A row opens the entry; Previous and Next step through the months.

/** "2026-01" as the first and last day of that month. */
export const monthRange = (ym) => {
  const [y, m] = String(ym).split('-').map(Number);
  const last = new Date(Date.UTC(y, m, 0)).getUTCDate();
  return { from: `${ym}-01`, to: `${ym}-${String(last).padStart(2, '0')}` };
};

/**
 * Oldest first, with the balance after each entry. Within a day the withdrawals come first: the
 * ledger does not know the hour cash arrived, and a cheque written to a vendor is entered as an
 * expense and a withdrawal together, which would otherwise dip the balance for one line.
 */
export const withRunningBalance = (entries, opening) => {
  const sorted = [...entries].sort((a, b) =>
    String(a.entryDate).localeCompare(String(b.entryDate))
    || (a.entryType === 'WITHDRAWAL' ? 0 : 1) - (b.entryType === 'WITHDRAWAL' ? 0 : 1)
    || String(a.createdAt || '').localeCompare(String(b.createdAt || '')));
  let balance = Number(opening || 0);
  return sorted.map((e) => {
    balance += (e.entryType === 'WITHDRAWAL' ? 1 : -1) * Number(e.amount || 0);
    return { ...e, balance };
  });
};

const Tile = ({ label, value, sign = '', tone, sub }) => (
  <div className={`rounded-xl border p-3 ${tone.border}`}>
    <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide">{label}</p>
    <p className={`text-lg font-bold mt-0.5 tabular-nums ${tone.text}`}>{sign}{formatCurrency(Math.abs(Number(value || 0)))}</p>
    <UrduAmount value={value} />
    {sub && <p className="text-[11px] text-gray-400">{sub}</p>}
  </div>
);

const TONES = {
  plain: { border: 'border-gray-200', text: 'text-gray-900' },
  in: { border: 'border-blue-200', text: 'text-blue-700' },
  out: { border: 'border-red-200', text: 'text-red-700' },
  good: { border: 'border-emerald-200', text: 'text-emerald-700' },
  bad: { border: 'border-red-300', text: 'text-red-700' },
};

const CashMonthModal = ({ month, previous, next, onStep, scoped, scopedName, departmentId, refreshKey, canEdit, onEdit, onClose }) => {
  // Kept with the month they belong to, so stepping to another month never shows the last one's
  // rows against the new opening, while a save in this month keeps its rows until the fresh ones
  // arrive.
  const [loaded, setLoaded] = useState({ month: null, entries: null });
  const [error, setError] = useState(null);
  const [detailId, setDetailId] = useState(null);
  const dialogRef = useDialog(onClose, { confirmDiscard: false });

  useEffect(() => {
    let alive = true;
    setError(null);
    const { from, to } = monthRange(month.month);
    expenseAPI.getEntries({ from, to, categoryId: departmentId })
      .then((data) => { if (alive) setLoaded({ month: month.month, entries: Array.isArray(data) ? data : [] }); })
      .catch((err) => { if (alive) setError(errorText(err, 'Could not load the month')); });
    return () => { alive = false; };
  }, [month.month, departmentId, refreshKey]);

  const entries = loaded.month === month.month ? loaded.entries : null;

  const rows = useMemo(() => withRunningBalance(entries || [], month.opening), [entries, month.opening]);
  const paging = usePagedRows(rows, { id: 'expense-cash-month', resetOn: [month.month] });

  // Where the spending went, by category, largest first.
  const spendBy = useMemo(() => {
    const byKey = new Map();
    (entries || []).filter((e) => e.entryType !== 'WITHDRAWAL').forEach((e) => {
      const name = (scoped ? e.categoryName : e.categoryPath) || 'No category';
      const g = byKey.get(name) || { name, amount: 0, count: 0 };
      g.amount += Number(e.amount || 0);
      g.count += 1;
      byKey.set(name, g);
    });
    return [...byKey.values()].sort((a, b) => b.amount - a.amount);
  }, [entries, scoped]);
  const spentTotal = spendBy.reduce((sum, g) => sum + g.amount, 0);

  const opening = Number(month.opening || 0);
  const closing = Number(month.closing || 0);
  const firstShort = opening >= 0 ? rows.find((r) => r.balance < 0) : null;

  return (
    <div ref={dialogRef} className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[92vh] flex flex-col overflow-hidden" role="dialog" aria-modal="true" aria-labelledby="cash-month-title">
        <div className="flex-shrink-0 px-6 py-4 bg-gradient-to-r from-navy-800 to-navy-700 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <h2 id="cash-month-title" className="text-lg font-semibold text-white truncate">{month.label}, {scopedName}</h2>
            <p className="text-xs text-navy-100 mt-0.5">
              {month.withdrawalCount} {Number(month.withdrawalCount) === 1 ? 'withdrawal' : 'withdrawals'} · {month.expenseCount} {Number(month.expenseCount) === 1 ? 'expense' : 'expenses'}
            </p>
          </div>
          <button aria-label="Close" onClick={onClose} className="p-2 rounded-lg hover:bg-white/10 transition-colors flex-shrink-0">
            <X className="h-5 w-5 text-white" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-5">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <Tile label="Opening" value={opening} sign={opening < 0 ? '- ' : ''} tone={opening < 0 ? TONES.bad : TONES.plain} sub="Cash in hand on the 1st" />
            <Tile label="Withdrawn" value={month.withdrawn} sign={Number(month.withdrawn) ? '+ ' : ''} tone={TONES.in} sub="Taken from the bank" />
            <Tile
              label="Spent"
              value={month.spent}
              sign={Number(month.spent) ? '- ' : ''}
              tone={TONES.out}
              sub={`Cash ${formatCurrency(month.cashSpent)} · cheque or transfer ${formatCurrency(month.chequeSpent)}`}
            />
            <Tile label="Closing" value={closing} sign={closing < 0 ? '- ' : ''} tone={closing < 0 ? TONES.bad : TONES.good} sub="Carried into the next month" />
          </div>

          {month.negative ? (
            <div role="alert" className="rounded-xl border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-900 flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <p>
                Closed short by <span className="font-semibold">{formatCurrency(Math.abs(closing))}</span>.{' '}
                {opening < 0
                  ? 'It opened short already, carried from an earlier month.'
                  : firstShort
                    ? <>The balance first went below zero on <span className="font-semibold">{formatDate(firstShort.entryDate)}</span>, with &ldquo;{firstShort.description}&rdquo;. A withdrawal before then may be missing.</>
                    : null}
              </p>
            </div>
          ) : month.overspent ? (
            <p className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
              Spent {formatCurrency(Number(month.spent) - Number(month.withdrawn))} more than was withdrawn, from the cash left over before this month.
            </p>
          ) : null}

          {error ? (
            <p className="text-sm text-red-600">{error}</p>
          ) : entries === null ? (
            <LoadingRow text="Loading the month..." />
          ) : (
            <>
              {spendBy.length > 0 && (
                <section aria-labelledby="cash-month-spend">
                  <h3 id="cash-month-spend" className="text-sm font-semibold text-gray-900 mb-2">Where it went</h3>
                  <ul className="rounded-xl border border-gray-200 divide-y divide-gray-100">
                    {spendBy.map((g) => {
                      const share = spentTotal > 0 ? (g.amount / spentTotal) * 100 : 0;
                      return (
                        <li key={g.name} className="px-4 py-2 flex items-center gap-3 text-sm">
                          <span className="flex-1 sm:flex-none sm:w-56 truncate text-gray-800" title={g.name}>{g.name}</span>
                          <span className="hidden sm:block flex-1 h-2 rounded-full bg-gray-100 overflow-hidden" aria-hidden="true">
                            <span className="block h-full bg-red-400" style={{ width: `${Math.max(share, 1)}%` }} />
                          </span>
                          <span className="w-12 text-right text-xs text-gray-500 tabular-nums">{Math.round(share)}%</span>
                          <span className="w-20 text-right text-xs text-gray-400 whitespace-nowrap">{g.count} {g.count === 1 ? 'entry' : 'entries'}</span>
                          <span className="w-32 text-right font-semibold text-gray-900 tabular-nums whitespace-nowrap">{formatCurrency(g.amount)}</span>
                        </li>
                      );
                    })}
                  </ul>
                </section>
              )}

              <section aria-labelledby="cash-month-entries" className="rounded-xl border border-gray-200">
                <h3 id="cash-month-entries" className="px-4 pt-3 text-sm font-semibold text-gray-900">
                  Every entry, oldest first
                  <span className="ml-2 text-xs font-normal text-gray-400">with the cash in hand after it · click one to open it</span>
                </h3>
                {rows.length === 0 ? (
                  <div className="text-center py-10 text-gray-400 text-sm">No entries in this month</div>
                ) : (
                  <>
                    <TablePager paging={paging} noun="entry" nounPlural="entries" className="px-4 mt-2" rounded={false} />
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-gray-100">
                          <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Date</th>
                          <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Description</th>
                          {!scoped && <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Department</th>}
                          {scoped && <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Category</th>}
                          <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Paid by</th>
                          <th className="px-4 py-2.5 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Amount</th>
                          <th className="px-4 py-2.5 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Cash in hand</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paging.pageRows.map((entry) => {
                          const open = () => setDetailId(entry.id);
                          const isWithdrawal = entry.entryType === 'WITHDRAWAL';
                          return (
                            <tr
                              key={entry.id}
                              tabIndex={0}
                              onClick={open}
                              onKeyDown={onActivateKey(open)}
                              className={`border-b border-gray-50 last:border-b-0 hover:bg-gray-50 transition-colors cursor-pointer ${ROW_FOCUS_RING}`}
                            >
                              <td className="px-4 py-2.5 text-sm text-gray-600 whitespace-nowrap">{formatDate(entry.entryDate)}</td>
                              <td className="px-4 py-2.5 text-sm">
                                <div className="flex items-center gap-2">
                                  <span className="text-gray-900 font-medium truncate max-w-xs" title={entry.description}>{entry.description}</span>
                                  {Number(entry.attachmentCount) > 0 && (
                                    <span className="inline-flex items-center gap-0.5 text-xs text-gray-400" title={`${entry.attachmentCount} file(s)`}>
                                      <Paperclip className="h-3.5 w-3.5" />{entry.attachmentCount}
                                    </span>
                                  )}
                                </div>
                                <div className="mt-0.5"><TypeBadge type={entry.entryType} /></div>
                              </td>
                              <td className="px-4 py-2.5 text-sm text-gray-600">
                                {(scoped ? entry.categoryName : entry.categoryPath) || <span className="text-gray-300">-</span>}
                              </td>
                              <td className="px-4 py-2.5">
                                <PaymentBadge mode={entry.paymentMode} />
                                {referenceOf(entry) && (
                                  <div className="text-xs text-gray-500 font-mono mt-1">
                                    {referenceOf(entry)}{entry.chequeBank ? ` · ${entry.chequeBank}` : ''}
                                  </div>
                                )}
                              </td>
                              <td className={`px-4 py-2.5 text-sm font-semibold text-right tabular-nums whitespace-nowrap ${isWithdrawal ? 'text-blue-700' : 'text-red-700'}`}>
                                {formatSignedAmount(entry)}
                              </td>
                              <td className={`px-4 py-2.5 text-sm text-right tabular-nums whitespace-nowrap ${entry.balance < 0 ? 'text-red-700 font-semibold' : 'text-gray-700'}`}>
                                {entry.balance < 0 ? '- ' : ''}{formatCurrency(Math.abs(entry.balance))}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                    <TablePager paging={paging} noun="entry" nounPlural="entries" position="bottom" className="px-4" />
                  </>
                )}
              </section>
            </>
          )}
        </div>

        <div className="flex-shrink-0 px-6 py-4 border-t border-gray-100 bg-gray-50 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <button
              type="button"
              disabled={!previous}
              onClick={() => onStep(previous)}
              className="flex items-center gap-1.5 px-3 py-2 text-sm text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-100 transition-colors font-medium disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="h-4 w-4" /> {previous ? previous.label : 'Previous month'}
            </button>
            <button
              type="button"
              disabled={!next}
              onClick={() => onStep(next)}
              className="flex items-center gap-1.5 px-3 py-2 text-sm text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-100 transition-colors font-medium disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {next ? next.label : 'Next month'} <ChevronRight className="h-4 w-4" />
            </button>
          </div>
          <button type="button" onClick={onClose} className="px-5 py-2.5 text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-100 transition-colors font-medium">
            Close
          </button>
        </div>
      </div>

      {detailId && (
        <EntryDetailModal
          entryId={detailId}
          canEdit={canEdit}
          onClose={() => setDetailId(null)}
          onEdit={(detail) => { setDetailId(null); onEdit(detail); }}
        />
      )}
    </div>
  );
};

export default CashMonthModal;
